import React, { useEffect, useMemo, useState } from 'react';
import { AIMetadata, AIOutput, AIResponseType } from 'system/AIResponseTypes';
import { AILoader } from './AILoader';
import { Box } from '@mui/system';
import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { Query } from 'system/query/Query';
import { AISummaryRenderer } from 'components/widgets/ai/AISummary/AISummaryRenderer';
import { AIPersonasRenderer } from 'components/widgets/ai/AISocialPersonas/AIPersonasRenderer';
import AIGradientBox from 'components/design/AI/AIGradientBox';
import { DashboardDocument } from 'components/resources/dashboards/DashboardDocument';
import { WithQueryWidgetProps } from './queryWidgetComposition/withQueryWidget';
import { getSingleActiveWidgetRequestByName, getSingleWidgetResponseByName } from 'system/widget/Widget';

export interface AIWidgetLoaderProps extends WithQueryWidgetProps {
  responseType: AIResponseType;
  canSaveWidgetCache?: boolean;
  dashboard?: DashboardDocument;
}

export function AIWidgetLoader(props: AIWidgetLoaderProps) {
  const [data, setData] = useState<AIOutput | undefined>(undefined);
  const [metadata, setMetadata] = useState<AIMetadata | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const [drilldownTitle, setDrilldownTitle] = useState<string | undefined>();
  const [drilldownQuery, setDrilldownQuery] = useState<Query | undefined>();

  // const queryIds = useMemo(() => {
  //   const request = getSingleActiveWidgetRequestByName(props.activeWidgetRequests, "ai-summary");
  //   if (!request) {
  //     return;
  //   }
  //   // Use the active query IDs from the base request if they exist
  //   return request.activeQueryIds;
  // }, [props.activeWidgetRequests]);

  const hasMatchingQueryHashes = useMemo(() => {
    const response = getSingleWidgetResponseByName(props.apiResponses, "ai-summary");
    const responseBody = response?.responseBody;
    if (props.dashboard && !props.dashboard._modifiable) {
      // If the dashboard is not modifiable, we can skip this check and just verify we have a hash
      // since we can't update it anyway.
      return !!responseBody?.hash;
    } else if (responseBody?.hash) {
      return responseBody.hash === props.widget?.data?.query_hash;
    }
  }, [props.widget?.data?.query_hash, props.apiResponses, props.dashboard]);

  // Load the cache, if it exists, we aren't actively trying to load, and it was generated with the same query hash(es)
  useEffect(() => {
    if(!isLoading && !isStreaming && !data && !metadata && hasMatchingQueryHashes && props.widget?.data?.meta) {
      // If this is a template dashboard, and the data was generated more than 24 hours ago, we should not use it
      const generatedDate = new Date(props.widget?.data?.generated_on || "");
      const oneDayAgo = new Date((new Date()).getTime() - (24 * 60 * 60 * 1000));
      if (props.dashboard?.template_type && generatedDate < oneDayAgo) {
        return;
      }
      setMetadata(props.widget?.data?.meta);
      setData(props.widget?.data?.summary);
    }
  }, [isLoading, isStreaming, data, metadata, hasMatchingQueryHashes, props.widget?.data?.summary, props.widget?.data?.meta]);

  const request = getSingleActiveWidgetRequestByName(props.activeWidgetRequests, "ai-summary");
  const requestBody = request?.requestInit?.body;
  const queryIds = request?.activeQueryIds || [];

  return (
  <AIGradientBox
    fillContainer={true}
    hideGradient={!isLoading && !isStreaming}>
    <Box sx={{ p: 2, position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'auto' }}>
      <Box className="ai-image-content">
        <AILoader
          queryIds={queryIds}
          queryBody={requestBody}
          saveToWidgetId={props.widget?.id}
          responseType={props.responseType}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isStreaming={isStreaming}
          setIsStreaming={setIsStreaming}
          metadata={metadata}
          setMetadata={setMetadata}
          data={data}
          setData={setData}
          showRerunButton={true}
          canSaveWidgetCache={props.canSaveWidgetCache}
        >
          {props.responseType === AIResponseType.SUMMARY &&
            <AISummaryRenderer
              data={data as AIOutput}
              metadata={metadata as AIMetadata}
              setDrilldownQuery={setDrilldownQuery}
              setDrilldownTitle={setDrilldownTitle}
              exportContentRef={props.widgetRef}
            />
          }
          {props.responseType === AIResponseType.PERSONAS &&
            <AIPersonasRenderer
              data={data as AIOutput}
              metadata={metadata as AIMetadata}
              setDrilldownQuery={setDrilldownQuery}
              setDrilldownTitle={setDrilldownTitle}
              exportContentRef={props.widgetRef}
            />
          }
        </AILoader>
        {drilldownQuery && drilldownTitle && props.queryInfo &&
          <DashboardQueryDrilldownDialog
            title={drilldownTitle}
            queryInfo={props.queryInfo}
            onClose={() => setDrilldownQuery(null)}
            activeQueryIds={queryIds || []}
            drilldownQuery={drilldownQuery}
            widgetRequestInfo={props.widget?.request_info}
          />
        }
      </Box>
    </Box>
  </AIGradientBox>
);
}